import { Box, FormField, Modal, Select, SelectProps, SpaceBetween, Tiles, Toggle } from '@amzn/awsui-components-core';
import React, { useState } from 'react';
import { LocalizationContextBuilder } from '@amzn/arb-tools';
import { useLocalizationContext } from '@amzn/react-arb-tools';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { useUserPreferences } from './useUserPreferences';
import { FontFamily, HomepageCardVisibility, PreferencesSection } from './user-preferences.types';
import styles from './UserPreferencesModal.module.scss';

import { eLocalStorageKeys } from 'src/constants/AppConstants';

const LOCALE_STORAGE_KEY = eLocalStorageKeys.Locale;

const LANGUAGE_OPTIONS: SelectProps.Option[] = [
  { value: 'en-US', label: 'English' },
  { value: 'hi-IN', label: 'हिन्दी (Hindi)' },
  { value: 'ta-IN', label: 'தமிழ் (Tamil)' },
  { value: 'te-IN', label: 'తెలుగు (Telugu)' },
  { value: 'kn-IN', label: 'ಕನ್ನಡ (Kannada)' },
  { value: 'ja-JP', label: '日本語 (Japanese)' },
  { value: 'de-DE', label: 'Deutsch (German)' },
  { value: 'fr-FR', label: 'Français (French)' },
  { value: 'es-ES', label: 'Español (Spanish)' },
  { value: 'zh-CN', label: '中文 (Chinese)' },
  { value: 'ko-KR', label: '한국어 (Korean)' },
  { value: 'pt-BR', label: 'Português (Portuguese)' }
];

const THEME_OPTIONS: { value: 'light' | 'dark'; label: string; description: string }[] = [
  { value: 'light', label: 'Light', description: 'Always use light theme' },
  { value: 'dark', label: 'Dark', description: 'Always use dark theme' }
];

const FONT_OPTIONS: SelectProps.Option[] = [
  { value: 'Ember Modern Text', label: 'Ember Modern Text (Default)' },
  { value: 'Amazon Ember', label: 'Amazon Ember' },
  { value: 'Amazon Ember Display', label: 'Amazon Ember Display' }
];

const CARD_OPTIONS: { key: keyof HomepageCardVisibility; label: string; description: string }[] = [
  { key: 'weather', label: 'Weather', description: 'Local weather conditions and forecast' },
  { key: 'stock', label: 'Stock Market', description: 'AMZN stock price and daily change' },
  { key: 'fxRates', label: 'FX Rates', description: 'Foreign exchange rates based on your location' },
  { key: 'newsTicker', label: 'News Ticker', description: 'Internal announcements and news' },
  { key: 'nflScores', label: 'NFL Scores', description: 'Live NFL scores and upcoming games' }
];

const SECTIONS: { id: PreferencesSection; label: string }[] = [
  { id: 'appearance', label: 'Appearance' },
  { id: 'language', label: 'Language' },
  { id: 'homepage-cards', label: 'Homepage Cards' }
];

interface UserPreferencesModalProps {
  visible: boolean;
  onDismiss: () => void;
}

const UserPreferencesModal: React.FC<UserPreferencesModalProps> = ({ visible, onDismiss }) => {
  const { preferences, setFontFamily, updateCardVisibility } = useUserPreferences();
  const { isDarkMode, setIsDarkMode } = useTheme();
  const { localizationContext, setLocalizationContext } = useLocalizationContext();
  const [activeSection, setActiveSection] = useState<PreferencesSection>('appearance');

  const currentLocale = localizationContext.getLocale();

  const handleThemeChange = (theme: 'light' | 'dark'): void => {
    document.documentElement.classList.add('theme-transitioning');
    setIsDarkMode(theme === 'dark');
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transitioning');
    }, 300);
  };

  const handleLanguageChange = (locale: string): void => {
    const newContext = new LocalizationContextBuilder().withLocale(locale).withDefaultLocale('en-US').build();
    setLocalizationContext(newContext);
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  };

  const renderAppearanceSection = (): React.ReactNode => (
    <SpaceBetween size="l">
      <FormField label="Theme" description="Choose how FinSuite looks to you.">
        <Tiles
          value={isDarkMode ? 'dark' : 'light'}
          onChange={({ detail }) => handleThemeChange(detail.value as 'light' | 'dark')}
          items={THEME_OPTIONS.map((opt) => ({
            value: opt.value,
            label: opt.label,
            description: opt.description
          }))}
        />
      </FormField>

      <FormField label="Font Family" description="Select the font used across the application.">
        <Select
          selectedOption={FONT_OPTIONS.find((o) => o.value === preferences.fontFamily) || null}
          onChange={({ detail }) => setFontFamily(detail.selectedOption.value as FontFamily)}
          options={FONT_OPTIONS}
        />
      </FormField>
    </SpaceBetween>
  );

  const renderHomepageCardsSection = (): React.ReactNode => {
    const allVisible = Object.values(preferences.cardVisibility).every(Boolean);
    const handleToggleAll = (checked: boolean): void => {
      CARD_OPTIONS.forEach((card) => updateCardVisibility(card.key, checked));
    };

    return (
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          Choose which cards appear on your homepage. Changes take effect immediately.
        </Box>
        <div className={styles.cardToggleRow}>
          <div className={styles.cardToggleInfo}>
            <Box fontWeight="bold" fontSize="body-m">
              Show All Cards
            </Box>
            <Box variant="p" color="text-body-secondary" fontSize="body-s">
              Toggle all homepage cards on or off
            </Box>
          </div>
          <Toggle checked={allVisible} onChange={({ detail }) => handleToggleAll(detail.checked)} />
        </div>
        {CARD_OPTIONS.map((card) => (
          <div key={card.key} className={styles.cardToggleRow}>
            <div className={styles.cardToggleInfo}>
              <Box fontWeight="bold" fontSize="body-m">
                {card.label}
              </Box>
              <Box variant="p" color="text-body-secondary" fontSize="body-s">
                {card.description}
              </Box>
            </div>
            <Toggle checked={preferences.cardVisibility[card.key]} onChange={({ detail }) => updateCardVisibility(card.key, detail.checked)} />
          </div>
        ))}
      </SpaceBetween>
    );
  };

  const renderActiveSection = (): React.ReactNode => {
    switch (activeSection) {
      case 'appearance':
        return renderAppearanceSection();
      case 'language':
        return (
          <SpaceBetween size="l">
            <FormField label="Language" description="Changes the homepage greeting and card labels. Other areas of the app are not yet translated.">
              <Select
                selectedOption={LANGUAGE_OPTIONS.find((o) => o.value === currentLocale) || LANGUAGE_OPTIONS[0]}
                onChange={({ detail }) => handleLanguageChange(detail.selectedOption.value || 'en-US')}
                options={LANGUAGE_OPTIONS}
                filteringType="auto"
              />
            </FormField>
          </SpaceBetween>
        );
      case 'homepage-cards':
        return renderHomepageCardsSection();
    }
  };

  return (
    <Modal visible={visible} onDismiss={onDismiss} header="Preferences" size="large">
      <div className={styles.modalLayout}>
        <nav className={styles.sideNav}>
          {SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              className={`${styles.navItem} ${activeSection === section.id ? styles.navItemActive : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </nav>
        <div className={styles.content}>{renderActiveSection()}</div>
      </div>
    </Modal>
  );
};

export default UserPreferencesModal;
